// Contrôle du menu latéral (Drawer) pour layout + guides interactifs.
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type LayoutShellControls = {
  isMenuOpen: boolean;
  /** true = viewport mobile (mask + fermeture au clic item). */
  isMobileShell: boolean;
  openMenu: () => void;
  closeMenu: () => void;
};

type LayoutShellContextValue = LayoutShellControls & {
  registerLayoutShell: (controls: LayoutShellControls | null) => void;
};

const noopControls: LayoutShellControls = {
  isMenuOpen: false,
  isMobileShell: false,
  openMenu: () => undefined,
  closeMenu: () => undefined,
};

const LayoutShellContext = createContext<LayoutShellContextValue | undefined>(undefined);

export const LayoutShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const controlsRef = useRef<LayoutShellControls>(noopControls);
  const [snapshot, setSnapshot] = useState({ isMenuOpen: false, isMobileShell: false });

  const registerLayoutShell = useCallback((controls: LayoutShellControls | null) => {
    if (!controls) {
      controlsRef.current = noopControls;
      setSnapshot({ isMenuOpen: false, isMobileShell: false });
      return;
    }
    controlsRef.current = controls;
    setSnapshot({
      isMenuOpen: controls.isMenuOpen,
      isMobileShell: controls.isMobileShell,
    });
  }, []);

  const openMenu = useCallback(() => {
    controlsRef.current.openMenu();
  }, []);

  const closeMenu = useCallback(() => {
    controlsRef.current.closeMenu();
  }, []);

  const value = useMemo(
    () => ({
      isMenuOpen: snapshot.isMenuOpen,
      isMobileShell: snapshot.isMobileShell,
      openMenu,
      closeMenu,
      registerLayoutShell,
    }),
    [snapshot.isMenuOpen, snapshot.isMobileShell, openMenu, closeMenu, registerLayoutShell]
  );

  return (
    <LayoutShellContext.Provider value={value}>{children}</LayoutShellContext.Provider>
  );
};

export function useLayoutShell(): LayoutShellContextValue {
  const ctx = useContext(LayoutShellContext);
  if (!ctx) {
    throw new Error('useLayoutShell must be used within LayoutShellProvider');
  }
  return ctx;
}

/** Safe hors provider (ex. routes sans shell). */
export function useOptionalLayoutShell(): LayoutShellControls {
  const ctx = useContext(LayoutShellContext);
  if (!ctx) return noopControls;
  return {
    isMenuOpen: ctx.isMenuOpen,
    isMobileShell: ctx.isMobileShell,
    openMenu: ctx.openMenu,
    closeMenu: ctx.closeMenu,
  };
}
